import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { CreditBadge } from '../components/CreditBadge';
import { DocManiaLogoImage } from '../components/DocManiaLogo';
import { ProposalStepIndicator } from '../components/proposals/ProposalStepIndicator';
import {
  RERUN_TEMPLATE_FALLBACK_MESSAGE,
  applyRerunTemplateToSearchParams,
  resolveRerunTemplate,
} from '../lib/rerunTemplateFallback';
import { toast } from 'sonner';
import { scrollToResultCard, formatResultCardMeta } from '../lib/documentGenerationUi';
import {
  ArrowLeft, ArrowRight, Loader2, CheckCircle, Download, Plus, Trash2,
  Briefcase, Building2, Users, FileText, Sparkles, ImageIcon, X,
  Target, Package, Coins, ClipboardList, Headphones, Landmark,
} from 'lucide-react';
import { CurrencySelector } from '../components/ui/CurrencySelector';
import { getCurrencyDisplayPrefix } from '../utils/currencies';
import './BusinessPlanWizardPage.css';

/* ─── Constants ────────────────────────────────────────────────────────── */
const PROPOSAL_TYPE_DEFAULT = 'Service Proposal';
const DRAFT_VERSION = 'v2';
const AUTOSAVE_MS = 30000;

const STEPS = [
  { id: 1, title: 'Business Info', icon: Building2, hint: 'Your company details' },
  { id: 2, title: 'Client Info', icon: Users, hint: 'Who you are proposing to' },
  { id: 3, title: 'Proposal Details', icon: FileText, hint: 'Type, jurisdiction & currency' },
  { id: 4, title: 'About Business', icon: Briefcase, hint: 'Credibility & positioning' },
  { id: 5, title: 'Challenges & Services', icon: Target, hint: 'Problem, scope & services' },
  { id: 6, title: 'Methodology & Deliverables', icon: ClipboardList, hint: 'Approach & deliverables' },
  { id: 7, title: 'Pricing & Timeline', icon: Coins, hint: 'Fees, VAT & milestones' },
  { id: 8, title: 'Payment & Why Us', icon: Landmark, hint: 'Terms, projects & differentiators' },
  { id: 9, title: 'Support & SLA', icon: Headphones, hint: 'Support, assumptions & generate' },
];

const WIZARD_STEP_COUNT = STEPS.length;

const INDUSTRY_OPTIONS = [
  'Construction', 'Real Estate', 'Healthcare', 'Education', 'Technology',
  'Finance', 'Legal Services', 'Consulting', 'Marketing & Advertising',
  'Cleaning Services', 'Logistics & Transportation', 'Hospitality',
  'Retail', 'Manufacturing', 'Energy', 'Agriculture', 'Nonprofit',
  'Government', 'Other',
];

const BILLING_BASIS_OPTIONS = [
  'Fixed Fee',
  'Hourly',
  'Monthly Retainer',
  'Per Unit',
];
const VAT_OPTIONS = ['0', '7.5', '10', '15', 'Custom'];
const PAYMENT_METHOD_OPTIONS = [
  'Bank Transfer',
  'Cash',
  'Mobile Money',
  'Bank Transfer or Mobile Money',
];
const PAYMENT_TERM_OPTIONS = [
  'Payment due upon project kickoff',
  '50% upfront, 50% on completion',
  '50% upfront deposit required before work begins',
  '100% payment required upfront',
  'Net 15 days from invoice date',
  'Net 30 days from invoice date',
  'Net 45 days from invoice date',
  'Monthly retainer — billed at end of each month',
  'Milestone-based payments',
  'Payment on delivery',
];
const PAYMENT_TERM_1_OPTIONS = PAYMENT_TERM_OPTIONS;
const PAYMENT_TERM_2_OPTIONS = PAYMENT_TERM_OPTIONS;
const LATE_PAYMENT_OPTIONS = [
  'A late payment fee of 5% per month applies to overdue invoices',
  'Overdue invoices attract 2.5% monthly interest',
  'Services may be suspended after 14 days of non-payment',
];
const REFUND_POLICY_OPTIONS = [
  'No refunds after work has commenced',
  'Deposits are non-refundable',
  'Refunds only applicable before project kickoff',
];
const COMMUNICATION_CHANNELS = [
  'Email',
  'WhatsApp',
  'Phone',
  'Email and WhatsApp',
  'Dedicated Account Manager',
];
const RESPONSE_TIME_OPTIONS = [
  'Within 24 hours',
  'Within 48 hours',
  'Same business day',
  'Within 4 business hours',
];
const SUPPORT_HOURS_OPTIONS = [
  'Monday–Friday 9am–5pm',
  'Monday–Saturday 8am–6pm',
  '24/7',
  'Business hours only',
];

const BLANK_SERVICE = { service_name: '', service_description: '' };
const BLANK_METHOD = { phase: '', activity: '', deliverable: '' };
const BLANK_DELIVERABLE = { item: '', format: '', delivery_date: '' };
const BLANK_PRICING = { service_item: '', billing_basis: '', rate: '', amount: '' };
const BLANK_TIMELINE = { milestone: '', description: '', date: '' };
const BLANK_PAST_PROJECT = { client: '', service: '', outcome: '' };

const defaultServices = () => [{ ...BLANK_SERVICE }, { ...BLANK_SERVICE }];

const SERVICE_NARRATIVE_SECTIONS = [
  { key: 'executive_summary', label: 'Executive Summary', rows: 6 },
  { key: 'about_us', label: 'About Us', rows: 5 },
  { key: 'client_challenges_and_objectives', label: 'Client Challenges & Objectives', rows: 5 },
  { key: 'methodology', label: 'Methodology Introduction', rows: 4 },
  { key: 'why_choose_us', label: 'Why Choose Us', rows: 4 },
  { key: 'closing_statement', label: 'Closing Statement', rows: 5 },
];

const INITIAL_DATA = {
  // Step 1 — Business info
  business_name: '',
  business_address: '',
  business_phone: '',
  business_email: '',
  business_website: '',
  signatory_name: '',
  signatory_title: '',
  // Step 2 — Client
  client_name: '',
  client_contact_person: '',
  client_title: '',
  // Step 3 — Proposal details
  proposal_type: PROPOSAL_TYPE_DEFAULT,
  jurisdiction: '',
  validity_days: '',
  notice_period_days: '',
  currency: 'NGN',
  // Step 4 — About your business
  about_us: '',
  company_name: '',
  registration_number: '',
  years_in_operation: '',
  industry_sector: '',
  industry_other: '',
  contact_person: '',
  // Step 5 — Challenges & services
  client_challenges: '',
  expected_benefits: '',
  proposed_services: defaultServices(),
  // Step 6 — Methodology & deliverables
  methodology_intro: '',
  methods: [{ ...BLANK_METHOD }, { ...BLANK_METHOD }],
  deliverables: [{ ...BLANK_DELIVERABLE }],
  success_metrics: '',
  // Step 7 — Pricing & timeline
  pricing: [{ ...BLANK_PRICING }, { ...BLANK_PRICING }],
  vat_rate: '0',
  vat_rate_custom: '',
  service_timeline: [{ ...BLANK_TIMELINE }],
  // Step 8 — Payment, past projects & why choose us
  payment_method: '',
  payment_term_1: '',
  payment_term_2: '',
  late_payment_policy: '',
  refund_policy: '',
  include_past_projects: 'no',
  past_projects: [{ ...BLANK_PAST_PROJECT }],
  why_choose_us: '',
  // Step 9 — Support & assumptions (+ AI preview / generate)
  communication_channel: '',
  response_time: '',
  support_hours: '',
  sla_commitment: '',
  project_assumptions: '',
  project_exclusions: '',
  // Optional pre-filled AI narrative (preview or prior run)
  executive_summary: '',
  client_challenges_and_objectives: '',
  methodology: '',
  services_intro: '',
  closing_statement: '',
};

const stripCommas = (v) => String(v ?? '').replaceAll(',', '').trim();

const formatMoney = (n, currency) => {
  const num = Number(n) || 0;
  const prefix = getCurrencyDisplayPrefix(currency);
  return `${prefix}${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/* ─── Shared UI helpers ────────────────────────────────────────────────── */
function RepeatableRow({ children, onRemove, removable }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-border bg-card/50 p-3">
      <div className="grid flex-1 gap-2">{children}</div>
      {removable && (
        <Button type="button" variant="ghost" size="icon" onClick={onRemove} className="text-muted-foreground hover:text-destructive">
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

function FieldRow({ label, hint, children, required }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium">
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

const LARGE_PROJECT_IMAGE_BYTES = 2 * 1024 * 1024;
const LARGE_PROJECT_IMAGE_WARNING =
  'This image is large and will be automatically resized to fit the cover page.';

function ImagePicker({ label, file, setFile, hint, testId, warnIfLargerThan }) {
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreview(null);
  }, [file]);

  const handle = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error('Image too large — keep under 5 MB.');
      return;
    }
    if (!f.type.startsWith('image/')) {
      toast.error('Please pick an image file.');
      return;
    }
    setFile(f);
  };

  return (
    <FieldRow label={label} hint={hint}>
      {preview ? (
        <div className="flex items-start gap-3">
          <div className="relative inline-block">
            <img src={preview} alt={label} className="h-32 w-32 rounded-lg border object-cover" />
            <button type="button" onClick={() => setFile(null)} className="absolute -top-2 -right-2 rounded-full bg-destructive p-1 text-white shadow" data-testid={`${testId}-remove`}>
              <X className="h-3 w-3" />
            </button>
          </div>
          <label className="flex h-32 flex-1 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-border bg-muted/30 px-3 text-center hover:bg-muted">
            <ImageIcon className="h-5 w-5 text-muted-foreground" />
            <span className="mt-1 text-xs text-muted-foreground">Replace</span>
            <input type="file" accept="image/png,image/jpeg,image/jpg" onChange={handle} className="hidden" data-testid={testId} />
          </label>
        </div>
      ) : (
        <label className="flex h-32 w-32 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-border bg-muted/40 hover:bg-muted">
          <ImageIcon className="h-6 w-6 text-muted-foreground" />
          <span className="mt-1 text-xs text-muted-foreground">Upload</span>
          <input type="file" accept="image/*" onChange={handle} className="hidden" data-testid={testId} />
        </label>
      )}
    </FieldRow>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-center justify-between rounded-md border bg-card/50 px-3 py-2">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-foreground truncate ml-2 max-w-[60%] text-right">{value}</span>
    </div>
  );
}

function AiActionButton({ label, loading, onClick, testId, icon: Icon = Sparkles }) {
  return (
    <Button type="button" variant="outline" size="sm" onClick={onClick} disabled={loading} data-testid={testId} className="shrink-0">
      {loading
        ? <><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Generating…</>
        : <><Icon className="mr-1 h-3 w-3" /> {label}</>}
    </Button>
  );
}

const confirmReplaceContent = (hasContent) => {
  if (!hasContent) return true;
  return window.confirm('Replace existing content?');
};

const tableRowHasContent = (rows, fields) =>
  (rows || []).some((r) => fields.some((f) => String(r[f] || '').trim()));

/* ─── Main page ────────────────────────────────────────────────────────── */
export default function ServiceProposalWizardPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialTemplateId = params.get('templateId');
  const [templateName, setTemplateName] = useState(
    () => params.get('templateName') || 'Service Proposal',
  );
  const rerunJobId = params.get('rerunJobId');
  const { user, api, refreshUser } = useAuth();

  const [templateId, setTemplateId] = useState(initialTemplateId);
  const [step, setStep] = useState(1);
  const [data, setData] = useState(INITIAL_DATA);
  const [logoFile, setLogoFile] = useState(null);
  const [projectImageFile, setProjectImageFile] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [polling, setPolling] = useState(false);
  const [processingStageMessage, setProcessingStageMessage] = useState('');
  const [jobResult, setJobResult] = useState(null);
  const [narrativeAllLoading, setNarrativeAllLoading] = useState(false);
  const [narrativeAiLoading, setNarrativeAiLoading] = useState({});
  const [fieldAiLoading, setFieldAiLoading] = useState({});
  const [suggestLoading, setSuggestLoading] = useState({});
  const [credits, setCredits] = useState(20);
  const [showDraftPill, setShowDraftPill] = useState(false);

  const draftRestoredRef = useRef(false);
  const rerunRestoreRef = useRef(false);

  const draftKey = useMemo(
    () => (templateId && user?.id ? `svc_proposal_${DRAFT_VERSION}_${user.id}_${templateId}` : null),
    [templateId, user?.id],
  );

  const fileToDataURI = (file) => new Promise((resolve, reject) => {
    if (!file) { resolve(null); return; }
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const dataURIToFile = (dataURI, filename) => {
    if (!dataURI || typeof dataURI !== 'string') return null;
    try {
      const [header, base64] = dataURI.split(',');
      const mime = (header.match(/data:(.+?);/) || [])[1] || 'image/png';
      const bin = atob(base64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      return new File([arr], filename, { type: mime });
    } catch {
      return null;
    }
  };

  const resolveIndustry = (sector, other) => {
    if ((sector || '').toLowerCase() === 'other' && (other || '').trim()) return other.trim();
    return sector || '';
  };

  const buildSubmitPayload = useCallback(() => {
    const industry = resolveIndustry(data.industry_sector, data.industry_other);
    return {
      ...data,
      industry_sector: industry,
      industry: industry,
      company_name: data.company_name || data.business_name,
      registration_number: data.registration_number,
      years_in_operation: data.years_in_operation,
      years: data.years_in_operation,
      rc_number: data.registration_number,
      signatory_name: data.signatory_name,
      signatory_title: data.signatory_title,
      business_signatory_name: data.signatory_name,
      client_contact_person: data.client_contact_person,
      client_title: data.client_title,
      client_signatory_name: data.client_contact_person,
      contact_person: data.contact_person || data.signatory_name,
      proposal_type: data.proposal_type || PROPOSAL_TYPE_DEFAULT,
      client_challenges: data.client_challenges,
      client_challenges_and_objectives:
        data.client_challenges_and_objectives || data.client_challenges,
      methodology: data.methodology || data.methodology_intro,
      methodology_intro: data.methodology_intro,
      communication_channel: data.communication_channel,
      support_channel: data.communication_channel,
    };
  }, [data]);

  const financials = useMemo(() => {
    const currency = data.currency || 'NGN';
    let subtotal = 0;
    for (const row of data.pricing || []) {
      const rate = Number(stripCommas(row.rate)) || 0;
      const amtRaw = row.amount;
      const amt = amtRaw !== '' && amtRaw != null ? Number(stripCommas(amtRaw)) : rate;
      subtotal += amt;
    }
    let vatRate = 0;
    if (String(data.vat_rate) === 'Custom') {
      vatRate = Number(stripCommas(data.vat_rate_custom)) || 0;
    } else {
      vatRate = Number(String(data.vat_rate).replace('%', '')) || 0;
    }
    const vatAmount = Math.round(subtotal * (vatRate / 100) * 100) / 100;
    const total = Math.round((subtotal + vatAmount) * 100) / 100;
    return { subtotal, vatAmount, total, vatRate, currency };
  }, [data.pricing, data.vat_rate, data.vat_rate_custom, data.currency]);

  const showPastProjects = ['yes', 'true', '1', 'on'].includes(
    String(data.include_past_projects || '').toLowerCase(),
  );

  /* ── Redirect if no template ───────────────────────────────────────── */
  useEffect(() => {
    if (!templateId && !rerunJobId) {
      toast.error('No template selected. Pick a category first.');
      navigate('/documents/business-proposal/categories');
    }
  }, [templateId, rerunJobId, navigate]);

  /* ── Credits ───────────────────────────────────────────────────────── */
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/documents/types');
        const meta = (res.data?.types || []).find((t) => t.id === 'service_proposal');
        if (meta?.credits) setCredits(meta.credits);
      } catch { /* default 20 */ }
    })();
  }, [api]);

  /* ── Rerun restore ─────────────────────────────────────────────────── */
  useEffect(() => {
    if (!rerunJobId || rerunRestoreRef.current) return;
    rerunRestoreRef.current = true;
    (async () => {
      try {
        const [rerunRes, logosRes] = await Promise.all([
          api.get(`/jobs/${rerunJobId}/rerun-data`),
          api.get(`/documents/${rerunJobId}/logos`).catch(() => ({ data: {} })),
        ]);
        const inputData = rerunRes.data?.input_data || {};
        const clean = Object.fromEntries(
          Object.entries(inputData).filter(([, v]) => v !== null && v !== undefined),
        );
        if (clean.industry && !clean.industry_sector) {
          if (!INDUSTRY_OPTIONS.includes(clean.industry)) {
            clean.industry_sector = 'Other';
            clean.industry_other = clean.industry;
          } else {
            clean.industry_sector = clean.industry;
          }
        }
        if (clean.client_industry && !INDUSTRY_OPTIONS.includes(clean.client_industry)) {
          clean.client_industry_other = clean.client_industry;
          clean.client_industry = 'Other';
        }
        setData((d) => ({
          ...d,
          ...clean,
          proposed_services: (clean.proposed_services?.length >= 2)
            ? clean.proposed_services
            : defaultServices(),
        }));
        const { templateId: tid, templateName: tname, fallbackUsed } = resolveRerunTemplate(
          rerunRes.data,
        );
        if (tid) {
          setTemplateId(tid);
          if (tname) setTemplateName(tname);
          const current = new URLSearchParams(window.location.search);
          const needsUrlUpdate = current.get('templateId') !== tid
            || (tname && current.get('templateName') !== tname);
          if (needsUrlUpdate) {
            const sp = applyRerunTemplateToSearchParams(current, {
              templateId: tid,
              templateName: tname,
            });
            navigate(`${window.location.pathname}?${sp.toString()}`, { replace: true });
          }
        }
        if (fallbackUsed) {
          toast.info(RERUN_TEMPLATE_FALLBACK_MESSAGE);
        }
        const lf = dataURIToFile(logosRes.data?.logo, 'restored-logo.png');
        const pf = dataURIToFile(logosRes.data?.project_image, 'restored-project.png');
        if (lf) setLogoFile(lf);
        if (pf) setProjectImageFile(pf);
        toast.success('Previous proposal data restored.');
      } catch {
        toast.error('Could not load previous proposal — starting fresh.');
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rerunJobId]);

  /* ── Draft restore ───────────────────────────────────────────────────── */
  useEffect(() => {
    if (!draftKey || rerunJobId) return;
    if (draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.data) {
        setData((d) => ({
          ...d,
          ...parsed.data,
          proposed_services: (parsed.data.proposed_services?.length >= 2)
            ? parsed.data.proposed_services
            : defaultServices(),
        }));
      }
      if (parsed?.step) setStep(parsed.step);
      const lf = dataURIToFile(parsed?.images?.logo, 'draft-logo.png');
      const pf = dataURIToFile(parsed?.images?.project_image, 'draft-project.png');
      if (lf) setLogoFile(lf);
      if (pf) setProjectImageFile(pf);
      setShowDraftPill(true);
      toast.success('Draft restored — pick up where you left off.');
    } catch {
      try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
    }
  }, [draftKey, rerunJobId]);

  /* ── Autosave ──────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!draftKey || rerunJobId || jobResult) return;
    const id = setInterval(async () => {
      try {
        let logoUri = null;
        let projUri = null;
        if (logoFile && logoFile.size <= 1024 * 1024) logoUri = await fileToDataURI(logoFile);
        if (projectImageFile && projectImageFile.size <= 1024 * 1024) projUri = await fileToDataURI(projectImageFile);
        const payload = JSON.stringify({
          data, step,
          images: { logo: logoUri, project_image: projUri },
          saved_at: new Date().toISOString(),
        });
        if (payload.length > 4 * 1024 * 1024) {
          localStorage.setItem(draftKey, JSON.stringify({ data, step, images: {}, saved_at: new Date().toISOString() }));
        } else {
          localStorage.setItem(draftKey, payload);
        }
      } catch { /* quota */ }
    }, AUTOSAVE_MS);
    return () => clearInterval(id);
  }, [draftKey, rerunJobId, data, step, logoFile, projectImageFile, jobResult]);

  const clearDraft = () => {
    if (!draftKey) return;
    try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
    setShowDraftPill(false);
    toast.info('Draft cleared.');
  };
  const dismissDraftPill = () => setShowDraftPill(false);

  const setField = (key, val) => setData((d) => ({ ...d, [key]: val }));
  const setRowField = (listKey, idx, key, val) =>
    setData((d) => ({ ...d, [listKey]: d[listKey].map((row, i) => (i === idx ? { ...row, [key]: val } : row)) }));
  const addRow = (listKey, blank) =>
    setData((d) => ({ ...d, [listKey]: [...d[listKey], { ...blank }] }));
  const removeRow = (listKey, idx, min = 1) =>
    setData((d) => ({ ...d, [listKey]: d[listKey].length <= min ? d[listKey] : d[listKey].filter((_, i) => i !== idx) }));

  /* ── Validation ────────────────────────────────────────────────────── */
  const validate = (s) => {
    if (s === 1) {
      if (!data.business_name.trim()) return 'Business name is required.';
    }
    if (s === 2) {
      if (!data.client_name.trim()) return 'Client name is required.';
    }
    if (s === 3) {
      if (!data.proposal_type.trim()) return 'Proposal type is required.';
    }
    if (s === 4) {
      if (!data.about_us.trim()) return 'About Us is required — a short draft is enough; suggestions will expand it.';
      if (!data.industry_sector) return 'Select your industry/sector.';
      if (data.industry_sector === 'Other' && !data.industry_other.trim()) return 'Specify your industry when Other is selected.';
    }
    if (s === 5) {
      const services = (data.proposed_services || []).filter((r) => (r.service_name || '').trim());
      if (services.length < 2) return 'Add at least two proposed services.';
      if (!data.client_challenges.trim()) return 'Describe the client challenges and objectives.';
    }
    if (s === 6) {
      const methods = (data.methods || []).filter((r) => (r.phase || '').trim());
      if (!methods.length) return 'Add at least one implementation phase.';
      const dels = (data.deliverables || []).filter((r) => (r.item || '').trim());
      if (!dels.length) return 'Add at least one deliverable.';
    }
    if (s === 7) {
      const priced = (data.pricing || []).filter((r) => (r.service_item || '').trim());
      if (!priced.length) return 'Add at least one pricing line item.';
    }
    if (s === 8) {
      if (!data.payment_term_1) return 'Select payment term 1.';
      if (!data.payment_method) return 'Select a payment method.';
    }
    if (s === 9) {
      if (!data.communication_channel) return 'Select a communication channel.';
      if (!data.sla_commitment.trim()) return 'SLA commitment is required.';
    }
    return null;
  };

  const goNext = () => {
    const err = validate(step);
    if (err) { toast.error(err); return; }
    setStep((n) => Math.min(WIZARD_STEP_COUNT, n + 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const goBack = () => {
    setStep((n) => Math.max(1, n - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getNarrativeValue = (sectionKey) => {
    if (sectionKey === 'client_challenges_and_objectives') {
      return data.client_challenges_and_objectives || data.client_challenges || '';
    }
    if (sectionKey === 'methodology') return data.methodology || data.methodology_intro || '';
    return data[sectionKey] || '';
  };

  const setNarrativeValue = (sectionKey, val) => {
    if (sectionKey === 'client_challenges_and_objectives') {
      setData((d) => ({
        ...d,
        client_challenges_and_objectives: val,
        client_challenges: val,
      }));
      return;
    }
    if (sectionKey === 'methodology') {
      setData((d) => ({ ...d, methodology: val, methodology_intro: val }));
      return;
    }
    setField(sectionKey, val);
  };

  const draftNarrativeSection = async (sectionKey) => {
    if (narrativeAiLoading[sectionKey]) return;
    setNarrativeAiLoading((s) => ({ ...s, [sectionKey]: true }));
    try {
      const res = await api.post('/documents/ai-service-proposal-section', {
        section: sectionKey,
        ...buildSubmitPayload(),
      });
      const text = res.data?.text || '';
      if (!text) throw new Error('Empty response');
      setNarrativeValue(sectionKey, text);
      toast.success('Draft ready — edit as needed.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not draft this section.');
    } finally {
      setNarrativeAiLoading((s) => ({ ...s, [sectionKey]: false }));
    }
  };

  const draftAllNarratives = async () => {
    if (narrativeAllLoading) return;
    setNarrativeAllLoading(true);
    try {
      const res = await api.post('/documents/ai-service-proposal-content', buildSubmitPayload());
      const content = res.data?.content || {};
      setData((d) => {
        const challenges = content.client_challenges_and_objectives || d.client_challenges_and_objectives;
        const method = content.methodology || d.methodology;
        return {
          ...d,
          executive_summary: content.executive_summary || d.executive_summary,
          about_us: content.about_us || d.about_us,
          client_challenges_and_objectives: challenges,
          client_challenges: challenges || d.client_challenges,
          methodology: method,
          methodology_intro: method || d.methodology_intro,
          why_choose_us: content.why_choose_us || d.why_choose_us,
          closing_statement: content.closing_statement || d.closing_statement,
        };
      });
      toast.success('All narratives drafted — review and edit before generating.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not draft narratives.');
    } finally {
      setNarrativeAllLoading(false);
    }
  };

  const suggestTextField = async (suggestKey, fieldKey, currentValue) => {
    if (!confirmReplaceContent(String(currentValue || '').trim())) return;
    if (suggestLoading[suggestKey]) return;
    setSuggestLoading((s) => ({ ...s, [suggestKey]: true }));
    try {
      const res = await api.post('/documents/ai-service-proposal-suggest', {
        suggest: suggestKey,
        ...buildSubmitPayload(),
      });
      const text = res.data?.text || '';
      if (!text) throw new Error('Empty response');
      setField(fieldKey, text);
      if (fieldKey === 'client_challenges') {
        setField('client_challenges_and_objectives', text);
      }
      toast.success('Suggestion ready — edit as needed.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Suggestion failed.');
    } finally {
      setSuggestLoading((s) => ({ ...s, [suggestKey]: false }));
    }
  };

  const suggestTableRows = async (suggestKey, listKey, mapRow, fieldNames) => {
    const rows = data[listKey] || [];
    if (!confirmReplaceContent(tableRowHasContent(rows, fieldNames))) return;
    if (suggestLoading[suggestKey]) return;
    setSuggestLoading((s) => ({ ...s, [suggestKey]: true }));
    try {
      const res = await api.post('/documents/ai-service-proposal-suggest', {
        suggest: suggestKey,
        ...buildSubmitPayload(),
      });
      const suggested = (res.data?.rows || []).map(mapRow);
      if (!suggested.length) throw new Error('Empty response');
      setField(listKey, suggested);
      toast.success('Suggestions ready — edit as needed.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Suggestion failed.');
    } finally {
      setSuggestLoading((s) => ({ ...s, [suggestKey]: false }));
    }
  };

  /* ── AI field drafts (SLA / assumptions / exclusions) ───────────────── */
  const draftFieldWithAi = async (field) => {
    if (fieldAiLoading[field]) return;
    setFieldAiLoading((s) => ({ ...s, [field]: true }));
    try {
      const res = await api.post('/documents/ai-service-proposal-field', {
        field,
        ...buildSubmitPayload(),
      });
      const text = res.data?.text || '';
      if (!text) throw new Error('Empty response');
      setField(field, text);
      toast.success('Draft ready — edit as needed.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not draft this field.');
    } finally {
      setFieldAiLoading((s) => ({ ...s, [field]: false }));
    }
  };

  /* ── Poll job ──────────────────────────────────────────────────────── */
  const pollJob = async (jobId) => {
    setPolling(true);
    setProcessingStageMessage('');
    for (let i = 0; i < 90; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const res = await api.get(`/jobs/${jobId}`);
        if (res.data.processing_stage_message) {
          setProcessingStageMessage(res.data.processing_stage_message);
        }
        if (res.data.status === 'preview_ready' || res.data.status === 'completed') {
          setJobResult(res.data);
          setPolling(false);
          setGenerating(false);
          setProcessingStageMessage('');
          refreshUser?.();
          if (draftKey) {
            try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
            setShowDraftPill(false);
          }
          toast.success('Your service proposal is ready.');
          scrollToResultCard();
          return;
        }
        if (res.data.status === 'failed') {
          setJobResult(res.data);
          setPolling(false);
          setGenerating(false);
          setProcessingStageMessage('');
          toast.error(res.data.error_message || 'Generation failed.');
          return;
        }
      } catch { /* keep polling */ }
    }
    setPolling(false);
    setGenerating(false);
    setProcessingStageMessage('');
    toast.error('Generation timed out. Please try again.');
  };

  const handleGenerate = async () => {
    if (generating) return;
    const err = validate(9);
    if (err) { toast.error(err); return; }
    if (!templateId) {
      toast.error('No template selected.');
      return;
    }
    setGenerating(true);
    setJobResult(null);
    try {
      const fd = new FormData();
      fd.append('service_type', 'document_generation');
      fd.append('document_type', 'service_proposal');
      fd.append('template_id', templateId);
      fd.append('input_data', JSON.stringify(buildSubmitPayload()));
      if (logoFile) fd.append('logo', logoFile);
      if (projectImageFile) fd.append('project_image', projectImageFile);

      const res = await api.post('/documents/service-proposal', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Generation started — this may take 20–30 seconds.');
      pollJob(res.data.job_id);
    } catch (err) {
      setGenerating(false);
      const status = err.response?.status;
      const detail = err.response?.data?.detail || 'Generation failed.';
      if (status === 402) {
        toast.error('Insufficient credits.');
        navigate('/credits');
      } else {
        toast.error(detail);
      }
    }
  };

  const handleDownload = async () => {
    if (!jobResult) return;
    let currentJob = jobResult;
    if (currentJob.status === 'preview_ready') {
      try {
        const fin = await api.post(`/documents/${currentJob.id}/finalize-download`);
        if (fin.data?.balance != null) {
          currentJob = { ...currentJob, status: 'completed' };
          setJobResult(currentJob);
        }
      } catch (err) {
        if (err.response?.status === 402) {
          toast.error('Not enough credits.');
          navigate('/credits');
        } else {
          toast.error(err.response?.data?.detail || 'Could not unlock document.');
        }
        return;
      }
    }
    try {
      const res = await api.get(`/storage/download/${currentJob.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${data.proposal_type || 'Service Proposal'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Download failed.');
    }
  };

  /* ── Step renderers ──────────────────────────────────────────────────── */
  const renderStep1 = () => (
    <div className="grid gap-4 md:grid-cols-2">
      <FieldRow label="Business Name" required>
        <Input value={data.business_name} onChange={(e) => setField('business_name', e.target.value)} data-testid="field-business_name" placeholder="Bright Cloud Consulting" />
      </FieldRow>
      <FieldRow label="Business Address">
        <Input value={data.business_address} onChange={(e) => setField('business_address', e.target.value)} data-testid="field-business_address" />
      </FieldRow>
      <FieldRow label="Business Email">
        <Input type="email" value={data.business_email} onChange={(e) => setField('business_email', e.target.value)} data-testid="field-business_email" />
      </FieldRow>
      <FieldRow label="Business Phone">
        <Input value={data.business_phone} onChange={(e) => setField('business_phone', e.target.value)} data-testid="field-business_phone" />
      </FieldRow>
      <FieldRow label="Business Website">
        <Input value={data.business_website} onChange={(e) => setField('business_website', e.target.value)} data-testid="field-business_website" />
      </FieldRow>
      <FieldRow label="Signatory Name">
        <Input value={data.signatory_name} onChange={(e) => setField('signatory_name', e.target.value)} data-testid="field-signatory_name" placeholder="Ada Obi" />
      </FieldRow>
      <FieldRow label="Signatory Title">
        <Input value={data.signatory_title} onChange={(e) => setField('signatory_title', e.target.value)} data-testid="field-signatory_title" placeholder="Managing Director" />
      </FieldRow>
      <FieldRow label="Currency" hint="Used for pricing totals in the document">
        <CurrencySelector value={data.currency} onChange={(v) => setField('currency', v)} />
      </FieldRow>
      <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6 pt-2">
        <ImagePicker label="Business Logo (optional)" file={logoFile} setFile={setLogoFile} hint="PNG or JPG — cover page logo." testId="upload-logo" />
        <ImagePicker
          label="Project/Cover Image (optional)"
          file={projectImageFile}
          setFile={setProjectImageFile}
          hint="PNG or JPG — cover page image."
          testId="upload-project-image"
          warnIfLargerThan={LARGE_PROJECT_IMAGE_BYTES}
        />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="grid gap-4 md:grid-cols-2">
      <FieldRow label="Client Name" required>
        <Input value={data.client_name} onChange={(e) => setField('client_name', e.target.value)} data-testid="field-client_name" />
      </FieldRow>
      <FieldRow label="Client Contact Person">
        <Input value={data.client_contact_person} onChange={(e) => setField('client_contact_person', e.target.value)} data-testid="field-client_contact_person" placeholder="Tunde Bello" />
      </FieldRow>
      <FieldRow label="Client Title">
        <Input value={data.client_title} onChange={(e) => setField('client_title', e.target.value)} data-testid="field-client_title" placeholder="CTO" />
      </FieldRow>
    </div>
  );

  const renderStep3 = () => (
    <div className="grid gap-4 md:grid-cols-2">
      <FieldRow label="Proposal Type" required hint='e.g. "IT Services Proposal"'>
        <Input value={data.proposal_type} onChange={(e) => setField('proposal_type', e.target.value)} data-testid="field-proposal_type" placeholder="IT Services Proposal" />
      </FieldRow>
      <FieldRow label="Jurisdiction">
        <Input value={data.jurisdiction} onChange={(e) => setField('jurisdiction', e.target.value)} data-testid="field-jurisdiction" placeholder="Lagos State, Nigeria" />
      </FieldRow>
      <FieldRow label="Proposal Validity (days)" hint="How long this proposal remains valid (default: 30)">
        <Input
          type="number"
          value={data.validity_days}
          onChange={(e) => setField('validity_days', e.target.value)}
          data-testid="field-validity_days"
          placeholder="30"
        />
      </FieldRow>
      <FieldRow label="Notice of Termination (days)" hint="Days' notice required to terminate the contract (default: 14)">
        <Input
          type="number"
          value={data.notice_period_days}
          onChange={(e) => setField('notice_period_days', e.target.value)}
          data-testid="field-notice_period_days"
          placeholder="14"
        />
      </FieldRow>
    </div>
  );

  const renderStep4 = () => (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="md:col-span-2">
        <FieldRow label="About Us" required hint="2–3 sentences describing your business — suggestions will expand this.">
          <Textarea rows={5} value={data.about_us} onChange={(e) => setField('about_us', e.target.value)} data-testid="field-about_us" />
        </FieldRow>
      </div>
      <FieldRow label="Company Name" hint="Legal entity name if different from trading name">
        <Input value={data.company_name} onChange={(e) => setField('company_name', e.target.value)} data-testid="field-company_name" placeholder="Bright Cloud Ltd" />
      </FieldRow>
      <FieldRow label="Registration Number" hint="e.g. RC123456, BN987654, EIN 12-3456789">
        <Input value={data.registration_number} onChange={(e) => setField('registration_number', e.target.value)} data-testid="field-registration_number" />
      </FieldRow>
      <FieldRow label="Years in Operation">
        <Input type="number" value={data.years_in_operation} onChange={(e) => setField('years_in_operation', e.target.value)} data-testid="field-years_in_operation" placeholder="5" />
      </FieldRow>
      <FieldRow label="Industry/Sector" required>
        <Select value={data.industry_sector} onValueChange={(v) => setField('industry_sector', v)}>
          <SelectTrigger data-testid="field-industry_sector"><SelectValue placeholder="Select industry" /></SelectTrigger>
          <SelectContent>
            {INDUSTRY_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      </FieldRow>
      {data.industry_sector === 'Other' && (
        <FieldRow label="Specify Industry" required>
          <Input value={data.industry_other} onChange={(e) => setField('industry_other', e.target.value)} data-testid="field-industry_other" />
        </FieldRow>
      )}
      <FieldRow label="Contact Person">
        <Input value={data.contact_person} onChange={(e) => setField('contact_person', e.target.value)} data-testid="field-contact_person" placeholder="Primary contact for this proposal" />
      </FieldRow>
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label className="text-sm font-medium">
            Client Challenges and Objectives <span className="text-destructive">*</span>
          </Label>
          <AiActionButton
            label="Get Suggestions"
            loading={suggestLoading.client_challenges}
            onClick={() => suggestTextField('client_challenges', 'client_challenges', data.client_challenges)}
            testId="ai-suggest-client_challenges"
          />
        </div>
        <Textarea
          rows={5}
          value={data.client_challenges}
          onChange={(e) => {
            const v = e.target.value;
            setData((d) => ({ ...d, client_challenges: v, client_challenges_and_objectives: v }));
          }}
          data-testid="field-client_challenges"
          placeholder="e.g. The client struggles with inconsistent cleaning quality across multiple facilities and needs a reliable partner..."
        />
        <p className="text-xs text-muted-foreground">Describe the client&apos;s problem — get suggestions for context-relevant content.</p>
      </div>
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label className="text-sm font-medium">Expected Benefits</Label>
          <AiActionButton
            label="Get Suggestions"
            loading={suggestLoading.expected_benefits}
            onClick={() => suggestTextField('expected_benefits', 'expected_benefits', data.expected_benefits)}
            testId="ai-suggest-expected_benefits"
          />
        </div>
        <Textarea
          rows={3}
          value={data.expected_benefits}
          onChange={(e) => setField('expected_benefits', e.target.value)}
          data-testid="field-expected_benefits"
          placeholder={'e.g. Improved workplace hygiene\nReduced staff illness\nConsistent waste management'}
        />
        <p className="text-xs text-muted-foreground">One benefit per line — rendered as bullet points in the PDF.</p>
      </div>
      <div className="space-y-3 rounded-lg border bg-muted/20 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-medium">
            Proposed Services <span className="text-destructive">*</span>{' '}
            <span className="text-xs font-normal text-muted-foreground">(minimum 2)</span>
          </div>
          <AiActionButton
            label="Suggest rows"
            loading={suggestLoading.services}
            onClick={() => suggestTableRows(
              'services',
              'proposed_services',
              (r) => ({ service_name: r.service_name || '', service_description: r.service_description || '' }),
              ['service_name', 'service_description'],
            )}
            testId="ai-suggest-services"
          />
        </div>
        {data.proposed_services.map((row, idx) => (
          <RepeatableRow key={idx} removable={data.proposed_services.length > 2} onRemove={() => removeRow('proposed_services', idx, 2)}>
            <div className="grid gap-3 md:grid-cols-2">
              <FieldRow label="Service Name">
                <Input value={row.service_name} onChange={(e) => setRowField('proposed_services', idx, 'service_name', e.target.value)} data-testid={`field-proposed_services-${idx}-service_name`} placeholder="e.g. Routine Office Cleaning" />
              </FieldRow>
              <FieldRow label="Description">
                <Textarea rows={2} value={row.service_description} onChange={(e) => setRowField('proposed_services', idx, 'service_description', e.target.value)} data-testid={`field-proposed_services-${idx}-service_description`} placeholder="e.g. Daily cleaning of offices, restrooms, and common areas" />
              </FieldRow>
            </div>
          </RepeatableRow>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => addRow('proposed_services', BLANK_SERVICE)} data-testid="add-proposed-service-btn">
          <Plus className="mr-1 h-3 w-3" /> Add service
        </Button>
      </div>
    </div>
  );

  const renderStep6 = () => (
    <div className="space-y-4">
      <FieldRow label="Methodology Introduction" hint="Brief overview — suggestions will expand based on your phases below.">
        <Textarea
          rows={3}
          value={data.methodology_intro}
          onChange={(e) => setField('methodology_intro', e.target.value)}
          data-testid="field-methodology_intro"
          placeholder="e.g. Our approach is structured around three phases: mobilization, routine delivery, and quality assurance..."
        />
      </FieldRow>
      <div className="space-y-3 rounded-lg border bg-muted/20 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-medium">Methodology</div>
          <AiActionButton
            label="Suggest rows"
            loading={suggestLoading.methodology}
            onClick={() => suggestTableRows(
              'methodology',
              'methods',
              (r) => ({ phase: r.phase || '', activity: r.activity || '', deliverable: r.deliverable || '' }),
              ['phase', 'activity', 'deliverable'],
            )}
            testId="ai-suggest-methodology"
          />
        </div>
        {data.methods.map((row, idx) => (
          <RepeatableRow key={idx} removable={data.methods.length > 1} onRemove={() => removeRow('methods', idx)}>
            <div className="grid gap-3 md:grid-cols-3">
              <FieldRow label="Phase">
                <Input value={row.phase} onChange={(e) => setRowField('methods', idx, 'phase', e.target.value)} data-testid={`field-methods-${idx}-phase`} placeholder="e.g. Phase 1 — Mobilization" />
              </FieldRow>
              <FieldRow label="Activity">
                <Input value={row.activity} onChange={(e) => setRowField('methods', idx, 'activity', e.target.value)} data-testid={`field-methods-${idx}-activity`} placeholder="e.g. Site inspection and staff deployment" />
              </FieldRow>
              <FieldRow label="Deliverable">
                <Input value={row.deliverable} onChange={(e) => setRowField('methods', idx, 'deliverable', e.target.value)} data-testid={`field-methods-${idx}-deliverable`} placeholder="e.g. Approved implementation plan" />
              </FieldRow>
            </div>
          </RepeatableRow>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => addRow('methods', BLANK_METHOD)} data-testid="add-method-btn">
          <Plus className="mr-1 h-3 w-3" /> Add phase
        </Button>
      </div>
      <div className="space-y-3 rounded-lg border bg-muted/20 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-medium">Deliverables</div>
          <AiActionButton
            label="Suggest rows"
            loading={suggestLoading.deliverables}
            onClick={() => suggestTableRows(
              'deliverables',
              'deliverables',
              (r) => ({ item: r.item || '', format: r.format || '', delivery_date: r.delivery_date || '' }),
              ['item', 'format', 'delivery_date'],
            )}
            testId="ai-suggest-deliverables"
          />
        </div>
        {data.deliverables.map((row, idx) => (
          <RepeatableRow key={idx} removable={data.deliverables.length > 1} onRemove={() => removeRow('deliverables', idx)}>
            <div className="grid gap-3 md:grid-cols-3">
              <FieldRow label="Item">
                <Input value={row.item} onChange={(e) => setRowField('deliverables', idx, 'item', e.target.value)} data-testid={`field-deliverables-${idx}-item`} placeholder="e.g. Monthly Performance Report" />
              </FieldRow>
              <FieldRow label="Format">
                <Input value={row.format} onChange={(e) => setRowField('deliverables', idx, 'format', e.target.value)} data-testid={`field-deliverables-${idx}-format`} placeholder="e.g. PDF Report" />
              </FieldRow>
              <FieldRow label="Delivery Date">
                <Input value={row.delivery_date} onChange={(e) => setRowField('deliverables', idx, 'delivery_date', e.target.value)} data-testid={`field-deliverables-${idx}-delivery_date`} placeholder="e.g. Monthly / Within 5 working days" />
              </FieldRow>
            </div>
          </RepeatableRow>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => addRow('deliverables', BLANK_DELIVERABLE)} data-testid="add-deliverable-btn">
          <Plus className="mr-1 h-3 w-3" /> Add deliverable
        </Button>
      </div>
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label className="text-sm font-medium">Success Metrics / KPIs</Label>
          <AiActionButton
            label="Get Suggestions"
            loading={suggestLoading.kpis}
            onClick={() => suggestTextField('kpis', 'success_metrics', data.success_metrics)}
            testId="ai-suggest-kpis"
          />
        </div>
        <Textarea
          rows={3}
          value={data.success_metrics}
          onChange={(e) => setField('success_metrics', e.target.value)}
          data-testid="field-success_metrics"
          placeholder={'e.g. 95% cleaning quality compliance\nZero HSE incidents\n100% restroom sanitation compliance'}
        />
        <p className="text-xs text-muted-foreground">One metric per line.</p>
      </div>
    </div>
  );

  const renderStep7 = () => (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Service item</TableHead>
            <TableHead>Billing basis</TableHead>
            <TableHead>Rate ({data.currency})</TableHead>
            <TableHead>Amount ({data.currency})</TableHead>
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.pricing.map((row, idx) => (
            <TableRow key={idx}>
              <TableCell className="align-top py-2">
                <Input value={row.service_item} onChange={(e) => setRowField('pricing', idx, 'service_item', e.target.value)} data-testid={`field-pricing-${idx}-service_item`} placeholder="e.g. Routine Facility Cleaning" />
              </TableCell>
              <TableCell className="align-top py-2">
                <Select value={row.billing_basis || ''} onValueChange={(v) => setRowField('pricing', idx, 'billing_basis', v)}>
                  <SelectTrigger data-testid={`field-pricing-${idx}-billing_basis`}><SelectValue placeholder="Basis" /></SelectTrigger>
                  <SelectContent>
                    {BILLING_BASIS_OPTIONS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell className="align-top py-2">
                <Input value={row.rate} onChange={(e) => setRowField('pricing', idx, 'rate', e.target.value)} data-testid={`field-pricing-${idx}-rate`} placeholder="e.g. 8500" />
              </TableCell>
              <TableCell className="align-top py-2">
                <Input value={row.amount} onChange={(e) => setRowField('pricing', idx, 'amount', e.target.value)} data-testid={`field-pricing-${idx}-amount`} placeholder="e.g. 8500 or leave blank to use rate" />
              </TableCell>
              <TableCell className="align-top py-2">
                <Button type="button" variant="ghost" size="icon" onClick={() => removeRow('pricing', idx)} disabled={data.pricing.length <= 1} className="text-muted-foreground hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Button type="button" variant="outline" size="sm" onClick={() => addRow('pricing', BLANK_PRICING)} data-testid="add-pricing-btn">
        <Plus className="mr-1 h-3 w-3" /> Add line item
      </Button>

      <div className="grid gap-4 md:grid-cols-2 rounded-lg border bg-muted/30 p-4">
        <FieldRow label="VAT / Tax Rate">
          <Select value={data.vat_rate} onValueChange={(v) => setField('vat_rate', v)}>
            <SelectTrigger data-testid="field-vat_rate"><SelectValue /></SelectTrigger>
            <SelectContent>
              {VAT_OPTIONS.map((v) => <SelectItem key={v} value={v}>{v === 'Custom' ? 'Custom %' : `${v}%`}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>
        {data.vat_rate === 'Custom' && (
          <FieldRow label="Custom VAT %">
            <Input value={data.vat_rate_custom} onChange={(e) => setField('vat_rate_custom', e.target.value)} data-testid="field-vat_rate_custom" placeholder="e.g. 5" />
          </FieldRow>
        )}
        <div className="md:col-span-2 grid gap-2 sm:grid-cols-3 text-sm">
          <div className="rounded-md border bg-card px-3 py-2">
            <div className="text-xs text-muted-foreground">Subtotal</div>
            <div className="font-semibold" data-testid="pricing-subtotal">{formatMoney(financials.subtotal, financials.currency)}</div>
          </div>
          <div className="rounded-md border bg-card px-3 py-2">
            <div className="text-xs text-muted-foreground">VAT ({financials.vatRate}%)</div>
            <div className="font-semibold" data-testid="pricing-vat">{formatMoney(financials.vatAmount, financials.currency)}</div>
          </div>
          <div className="rounded-md border bg-primary/10 px-3 py-2">
            <div className="text-xs text-muted-foreground">Total</div>
            <div className="font-semibold text-primary" data-testid="pricing-total">{formatMoney(financials.total, financials.currency)}</div>
          </div>
        </div>
      </div>

      <div className="space-y-3 rounded-lg border bg-muted/20 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-medium">Service Timeline / Milestones</div>
          <AiActionButton
            label="Suggest rows"
            loading={suggestLoading.timeline}
            onClick={() => suggestTableRows(
              'timeline',
              'service_timeline',
              (r) => ({
                milestone: r.milestone || '',
                description: r.description || '',
                date: r.target_date || r.date || '',
              }),
              ['milestone', 'description', 'date'],
            )}
            testId="ai-suggest-timeline"
          />
        </div>
        {data.service_timeline.map((row, idx) => (
          <RepeatableRow key={idx} removable={data.service_timeline.length > 1} onRemove={() => removeRow('service_timeline', idx)}>
            <div className="grid gap-3 md:grid-cols-3">
              <FieldRow label="Milestone">
                <Input value={row.milestone} onChange={(e) => setRowField('service_timeline', idx, 'milestone', e.target.value)} data-testid={`field-service_timeline-${idx}-milestone`} placeholder="e.g. Contract Mobilization" />
              </FieldRow>
              <FieldRow label="Description">
                <Input value={row.description} onChange={(e) => setRowField('service_timeline', idx, 'description', e.target.value)} data-testid={`field-service_timeline-${idx}-description`} placeholder="e.g. Site familiarization and staff deployment" />
              </FieldRow>
              <FieldRow label="Target Date">
                <Input value={row.date} onChange={(e) => setRowField('service_timeline', idx, 'date', e.target.value)} data-testid={`field-service_timeline-${idx}-date`} placeholder="e.g. Within 5 working days of contract award" />
              </FieldRow>
            </div>
          </RepeatableRow>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => addRow('service_timeline', BLANK_TIMELINE)} data-testid="add-timeline-btn">
          <Plus className="mr-1 h-3 w-3" /> Add milestone
        </Button>
      </div>
    </div>
  );

  const renderStep8 = () => (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <FieldRow label="Payment Method" required>
          <Select value={data.payment_method} onValueChange={(v) => setField('payment_method', v)}>
            <SelectTrigger data-testid="field-payment_method"><SelectValue placeholder="Select method" /></SelectTrigger>
            <SelectContent>
              {PAYMENT_METHOD_OPTIONS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>
        <FieldRow label="Payment Term 1" required>
          <Select value={data.payment_term_1} onValueChange={(v) => setField('payment_term_1', v)}>
            <SelectTrigger data-testid="field-payment_term_1"><SelectValue placeholder="Select term" /></SelectTrigger>
            <SelectContent>
              {PAYMENT_TERM_1_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>
        <FieldRow label="Payment Term 2">
          <Select value={data.payment_term_2} onValueChange={(v) => setField('payment_term_2', v)}>
            <SelectTrigger data-testid="field-payment_term_2"><SelectValue placeholder="Select term" /></SelectTrigger>
            <SelectContent>
              {PAYMENT_TERM_2_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>
        <FieldRow label="Late Payment Policy">
          <Select value={data.late_payment_policy} onValueChange={(v) => setField('late_payment_policy', v)}>
            <SelectTrigger data-testid="field-late_payment_policy"><SelectValue placeholder="Select policy" /></SelectTrigger>
            <SelectContent>
              {LATE_PAYMENT_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>
        <FieldRow label="Refund Policy">
          <Select value={data.refund_policy} onValueChange={(v) => setField('refund_policy', v)}>
            <SelectTrigger data-testid="field-refund_policy"><SelectValue placeholder="Select policy" /></SelectTrigger>
            <SelectContent>
              {REFUND_POLICY_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>
      </div>
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label className="text-sm font-medium">Why Choose Us</Label>
          <AiActionButton
            label="Get Suggestions"
            loading={suggestLoading.why_choose_us}
            onClick={() => suggestTextField('why_choose_us', 'why_choose_us', data.why_choose_us)}
            testId="ai-suggest-why_choose_us"
          />
        </div>
        <Textarea
          rows={4}
          value={data.why_choose_us}
          onChange={(e) => setField('why_choose_us', e.target.value)}
          data-testid="field-why_choose_us"
          placeholder={'15 years delivering enterprise migrations\nCertified partner network'}
        />
        <p className="text-xs text-muted-foreground">One reason per line — suggestions will polish into bullet points in the document.</p>
      </div>
      <FieldRow label="Include Past Projects / Case Studies?">
        <Select value={data.include_past_projects} onValueChange={(v) => setField('include_past_projects', v)}>
          <SelectTrigger data-testid="field-include_past_projects"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="no">No</SelectItem>
            <SelectItem value="yes">Yes</SelectItem>
          </SelectContent>
        </Select>
      </FieldRow>
      {showPastProjects && (
        <div className="space-y-3 rounded-lg border bg-muted/20 p-3">
          <div className="text-sm font-medium">Past Projects</div>
          {data.past_projects.map((row, idx) => (
            <RepeatableRow key={idx} removable={data.past_projects.length > 1} onRemove={() => removeRow('past_projects', idx)}>
              <div className="grid gap-3 md:grid-cols-3">
                <FieldRow label="Client">
                  <Input value={row.client} onChange={(e) => setRowField('past_projects', idx, 'client', e.target.value)} data-testid={`field-past_projects-${idx}-client`} />
                </FieldRow>
                <FieldRow label="Service">
                  <Input value={row.service} onChange={(e) => setRowField('past_projects', idx, 'service', e.target.value)} data-testid={`field-past_projects-${idx}-service`} />
                </FieldRow>
                <FieldRow label="Status / Outcome">
                  <Input value={row.outcome} onChange={(e) => setRowField('past_projects', idx, 'outcome', e.target.value)} data-testid={`field-past_projects-${idx}-outcome`} placeholder="Ongoing or Completed" />
                </FieldRow>
              </div>
            </RepeatableRow>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => addRow('past_projects', BLANK_PAST_PROJECT)} data-testid="add-past-project-btn">
            <Plus className="mr-1 h-3 w-3" /> Add project
          </Button>
        </div>
      )}
    </div>
  );

  const serviceCount = (data.proposed_services || []).filter((r) => (r.service_name || '').trim()).length;
  const pricingCount = (data.pricing || []).filter((r) => (r.service_item || '').trim()).length;

  const renderStep9 = () => (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <FieldRow label="Communication Channel" required>
          <Select value={data.communication_channel} onValueChange={(v) => setField('communication_channel', v)}>
            <SelectTrigger data-testid="field-communication_channel"><SelectValue placeholder="Select channel" /></SelectTrigger>
            <SelectContent>
              {COMMUNICATION_CHANNELS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>
        <FieldRow label="Response Time">
          <Select value={data.response_time} onValueChange={(v) => setField('response_time', v)}>
            <SelectTrigger data-testid="field-response_time"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              {RESPONSE_TIME_OPTIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>
        <FieldRow label="Support Hours">
          <Select value={data.support_hours} onValueChange={(v) => setField('support_hours', v)}>
            <SelectTrigger data-testid="field-support_hours"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              {SUPPORT_HOURS_OPTIONS.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>
      </div>
      {[
        { key: 'sla_commitment', label: 'SLA Commitment', required: true, rows: 2 },
        { key: 'project_assumptions', label: 'Project Assumptions', required: false, rows: 4 },
        { key: 'project_exclusions', label: 'Project Exclusions', required: false, rows: 4 },
      ].map(({ key, label, required, rows }) => (
        <div key={key} className="space-y-2 rounded-lg border bg-card/50 p-3" data-testid={`ai-field-${key}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Label className="text-sm font-medium">
              {label} {required && <span className="text-destructive">*</span>}
            </Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => draftFieldWithAi(key)}
              disabled={fieldAiLoading[key]}
              data-testid={`draft-ai-${key}`}
            >
              {fieldAiLoading[key]
                ? <><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Drafting…</>
                : <><Sparkles className="mr-1 h-3 w-3" /> Get Suggestions</>}
            </Button>
          </div>
          <Textarea
            rows={rows}
            value={data[key]}
            onChange={(e) => setField(key, e.target.value)}
            data-testid={`field-${key}`}
          />
          <p className="text-xs text-muted-foreground">Click Get Suggestions to generate, then edit as needed.</p>
        </div>
      ))}

      <div className="space-y-4 rounded-lg border bg-muted/20 p-4" data-testid="narrative-sections">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium">Narrative sections</p>
            <p className="text-xs text-muted-foreground">
              Get Suggestions or leave blank — empty sections are auto-filled during generation.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={draftAllNarratives}
            disabled={narrativeAllLoading}
            data-testid="draft-all-narratives-btn"
            className="shrink-0"
          >
            {narrativeAllLoading
              ? <><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Drafting all…</>
              : <><Sparkles className="mr-1 h-3 w-3" /> Draft All Narratives</>}
          </Button>
        </div>
        {SERVICE_NARRATIVE_SECTIONS.map(({ key, label, rows }) => (
          <div key={key} className="space-y-2 rounded-lg border bg-card/50 p-3" data-testid={`narrative-${key}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label className="text-sm font-medium">{label}</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => draftNarrativeSection(key)}
                disabled={narrativeAiLoading[key]}
                data-testid={`draft-ai-${key}`}
              >
                {narrativeAiLoading[key]
                  ? <><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Drafting…</>
                  : <><Sparkles className="mr-1 h-3 w-3" /> Get Suggestions</>}
              </Button>
            </div>
            <Textarea
              rows={rows}
              value={getNarrativeValue(key)}
              onChange={(e) => setNarrativeValue(key, e.target.value)}
              data-testid={`field-narrative-${key}`}
            />
            <p className="text-xs text-muted-foreground">
              Click Get Suggestions to generate, then edit as needed.
            </p>
          </div>
        ))}
      </div>

      <Card data-testid="review-summary-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Review before generating</CardTitle>
          <CardDescription>
            {credits} credits are deducted when you generate.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-2 sm:grid-cols-2">
          <SummaryRow label="Business" value={data.business_name || '—'} />
          <SummaryRow label="Client" value={data.client_name || '—'} />
          <SummaryRow label="Proposal type" value={data.proposal_type || '—'} />
          <SummaryRow label="Services" value={`${serviceCount}`} />
          <SummaryRow label="Pricing rows" value={`${pricingCount}`} />
          <SummaryRow label="Total" value={formatMoney(financials.total, financials.currency)} />
          <SummaryRow label="Logo" value={logoFile ? '✓' : '—'} />
          <SummaryRow label="Project image" value={projectImageFile ? '✓' : '—'} />
          <SummaryRow label="Template" value={templateName} />
        </CardContent>
      </Card>

      {jobResult && (jobResult.status === 'preview_ready' || jobResult.status === 'completed') ? (
        <Card className="border-primary/30 bg-primary/5" data-testid="result-card">
          <CardContent className="py-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-primary" />
                <div>
                  <p className="font-semibold">Your service proposal is ready</p>
                  <p className="text-sm text-muted-foreground">
                    {formatResultCardMeta(jobResult, credits) || `${credits} credits deducted when generated.`}
                  </p>
                </div>
              </div>
              <Button onClick={handleDownload} data-testid="download-btn" className="glow-button">
                <Download className="mr-2 h-4 w-4" />
                {jobResult.status === 'preview_ready' ? `Unlock & Download — ${credits} credits` : 'Download PDF'}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-5 flex flex-col sm:flex-row sm:items-center justify-end gap-3">
            <Button onClick={handleGenerate} disabled={generating || polling} className="glow-button px-8" data-testid="generate-btn">
              {generating
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{polling ? (processingStageMessage || 'Building…') : 'Submitting…'}</>
                : <><FileText className="mr-2 h-4 w-4" /> Generate Proposal</>}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );

  const stepRenderers = {
    1: renderStep1, 2: renderStep2, 3: renderStep3, 4: renderStep4,
    5: renderStep5, 6: renderStep6, 7: renderStep7, 8: renderStep8, 9: renderStep9,
  };

  const currentStep = STEPS[step - 1];
  const Icon = currentStep.icon;

  if (!templateId && !rerunJobId) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background" data-testid="service-wizard-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-16" data-testid="service-proposal-wizard">
      <header className="border-b border-border bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-3">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2" data-testid="back-to-dashboard">
            <DocManiaLogoImage size={28} />
          </button>
          <div className="flex items-center gap-3">
            <CreditBadge balance={user?.credit_balance || 0} />
            <Button variant="ghost" size="sm" onClick={() => navigate('/documents/business-proposal/categories')}>
              <ArrowLeft className="mr-1 h-3 w-3" /> Categories
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <button onClick={() => navigate('/documents/business-proposal/categories')} className="mb-2 inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
              <ArrowLeft className="mr-1 h-3 w-3" /> Change template
            </button>
            <div className="bp-header-title">
              <h1 className="flex items-center gap-2 text-2xl font-semibold sm:text-3xl">
                <Package className="h-6 w-6 text-primary" /> Service Proposal Builder
              </h1>
              <p>Complete all steps to generate a professional service proposal</p>
            </div>
            <p className="text-sm text-muted-foreground">{currentStep.hint} · Step {step} of {WIZARD_STEP_COUNT}</p>
          </div>
          <Badge variant="secondary" className="hidden sm:inline-flex">
            {credits} credits on generate
          </Badge>
        </div>

        <ProposalStepIndicator steps={STEPS} step={step} />

        {showDraftPill && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm" data-testid="draft-restored-pill">
            <div className="flex items-center gap-2 text-emerald-900">
              <CheckCircle className="h-4 w-4" />
              <span><strong>Draft restored</strong> — auto-saved every 30 seconds.</span>
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={dismissDraftPill} data-testid="dismiss-draft-pill">
                Dismiss
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={clearDraft} data-testid="clear-draft-btn" className="text-destructive hover:text-destructive">
                Reset draft
              </Button>
            </div>
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Icon className="h-5 w-5 text-primary" /> {currentStep.title}
            </CardTitle>
          </CardHeader>
          <CardContent>{stepRenderers[step]()}</CardContent>
        </Card>

        <div className="flex items-center justify-between gap-3 pt-2">
          <Button type="button" variant="outline" onClick={goBack} disabled={step === 1 || generating} data-testid="wizard-back-btn">
            <ArrowLeft className="mr-1 h-4 w-4" /> Back
          </Button>
          <div className="text-xs text-muted-foreground">{step}/{WIZARD_STEP_COUNT}</div>
          {step < WIZARD_STEP_COUNT ? (
            <Button type="button" onClick={goNext} disabled={generating} data-testid="wizard-next-btn" className="glow-button">
              Next <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          ) : (
            <span />
          )}
        </div>
      </main>
    </div>
  );
}
